import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { task, timeout } from 'ember-concurrency';
import { TrackedAsyncData } from 'ember-async-data';
import t from 'ember-intl/helpers/t';
import { or, not } from 'ember-truth-helpers';
import { on } from '@ember/modifier';
import FaIcon from '@fortawesome/ember-fontawesome/components/fa-icon';
import { fn } from '@ember/helper';
import UserProfileCohortsManager from './user-profile-cohorts-manager';
import set from 'ember-set-helper/helpers/set';
import UserProfileCohortsDetails from './user-profile-cohorts-details';
import {
  faSpinner,
  faCheck,
  faArrowRotateLeft,
  faPenToSquare,
} from '@fortawesome/free-solid-svg-icons';
import BigSaveCancelButtons from './big-save-cancel-buttons';

export default class UserProfileCohortsComponent extends Component {
  @service currentUser;
  @service permissionChecker;
  @service store;

  @tracked hasSavedRecently = false;
  @tracked newPrimaryCohort = false;
  @tracked cohortsToAdd = [];
  @tracked cohortsToRemove = [];
  @tracked selectedSchool = null;

  @cached
  get allSchoolsData() {
    return new TrackedAsyncData(this.store.findAll('school'));
  }

  @cached
  get schoolPermissionsData() {
    return new TrackedAsyncData(
      Promise.all(
        this.allSchoolsData.value.map(async (school) => {
          return {
            school,
            canUpdate: await this.permissionChecker.canUpdateUserInSchool(school),
          };
        }),
      ),
    );
  }

  get selectedableSchools() {
    if (!this.allSchoolsData.isResolved || !this.schoolPermissionsData.isResolved) {
      return [];
    }

    return this.schoolPermissionsData.value
      .filter((obj) => obj.canUpdate)
      .map(({ school }) => school);
  }

  @cached
  get userData() {
    return new TrackedAsyncData(this.currentUser.getModel());
  }

  get user() {
    return this.userData.isResolved ? this.userData.value : null;
  }

  get currentSchool() {
    if (this.selectedSchool) {
      return this.selectedSchool;
    }

    const schoolId = this.user?.belongsTo('school').id();

    return this.selectedableSchools.find((school) => school.id === schoolId);
  }

  @cached
  get primaryCohortData() {
    return new TrackedAsyncData(this.args.user.primaryCohort);
  }

  get primaryCohort() {
    return this.primaryCohortData.isResolved ? this.primaryCohortData.value : null;
  }

  @cached
  get cohortsData() {
    return new TrackedAsyncData(this.args.user.cohorts);
  }

  get secondaryCohorts() {
    if (!this.cohortsData.isResolved || !this.primaryCohortData.isResolved) {
      return [];
    }

    const cohorts = [...this.cohortsData.value, ...this.cohortsToAdd].reduce((list, c) => {
      if (!this.cohortsToRemove.includes(c) && !list.includes(c)) {
        list.push(c);
      }

      return list;
    }, []);

    if (!this.currentPrimaryCohort) {
      return cohorts;
    }
    return cohorts.filter((cohort) => {
      return cohort.id !== this.currentPrimaryCohort.id;
    });
  }

  get isLoaded() {
    return (
      this.primaryCohortData.isResolved &&
      this.cohortsData.isResolved &&
      this.allSchoolsData.isResolved &&
      this.schoolPermissionsData.isResolved &&
      this.userData.isResolved
    );
  }

  get currentPrimaryCohort() {
    if (this.newPrimaryCohort === null) {
      return null;
    }
    if (this.newPrimaryCohort === false) {
      return this.primaryCohort;
    }

    return this.newPrimaryCohort;
  }

  @action
  addSecondaryCohort(cohort) {
    this.cohortsToAdd = [...this.cohortsToAdd, cohort];
    this.cohortsToRemove = this.cohortsToRemove.filter((c) => c !== cohort);
  }

  @action
  removeSecondaryCohort(cohort) {
    this.cohortsToRemove = [...this.cohortsToRemove, cohort];
    this.cohortsToAdd = this.cohortsToAdd.filter((c) => c !== cohort);
  }

  cancel = task({ restartable: true }, async () => {
    this.reset();
    this.args.setIsManaging(false);
  });

  reset() {
    this.cohortsToAdd = [];
    this.cohortsToRemove = [];
    this.newPrimaryCohort = false;
  }

  save = task({ drop: true }, async () => {
    const cohorts = [this.currentPrimaryCohort, ...this.secondaryCohorts];
    this.args.user.primaryCohort = this.currentPrimaryCohort;
    this.args.user.cohorts = cohorts.filter(Boolean);
    await this.args.user.save();
    this.reset();
    this.args.setIsManaging(false);
    this.hasSavedRecently = true;
    await timeout(500);
    this.hasSavedRecently = false;
  });
  <template>
    <div
      class="user-profile-cohorts small-component{{if
          this.hasSavedRecently
          ' has-saved'
          ' has-not-saved'
        }}"
      data-test-user-profile-cohorts
      ...attributes
    >
      {{#if this.isLoaded}}
        <div class="user-profile-cohorts-header">
          <h2 class="title" data-test-title>
            {{t "general.cohorts"}}
          </h2>
          <div class="actions">
            {{#if @isManaging}}
              <BigSaveCancelButtons
                @save={{this.save}}
                @cancel={{this.cancel.perform}}
                @disableSave={{or
                  this.save.isRunning
                  this.cancel.isRunning
                  (not this.currentPrimaryCohort)
                }}
                @disableCancel={{or this.save.isRunning this.cancel.isRunning}}
                as |save cancel disableSave disableCancel|
              >
                <button
                  aria-label={{t "general.save"}}
                  type="button"
                  class="bigsave"
                  disabled={{disableSave}}
                  title={{if
                    (or this.save.isRunning this.cancel.isRunning (not this.currentPrimaryCohort))
                    (t "general.disabledNoPrimaryCohort")
                    (t "general.save")
                  }}
                  {{on "click" save}}
                  data-test-save
                >
                  <FaIcon
                    @icon={{if this.save.isRunning faSpinner faCheck}}
                    @spin={{if this.save.isRunning true false}}
                    @fixedWidth={{true}}
                  />
                </button>
                <button
                  aria-label={{t "general.cancel"}}
                  type="button"
                  class="bigcancel"
                  disabled={{disableCancel}}
                  {{on "click" cancel}}
                  data-test-cancel
                >
                  <FaIcon @icon={{faArrowRotateLeft}} @fixedWidth={{true}} />
                </button>
              </BigSaveCancelButtons>
            {{else if @isManageable}}
              <button
                aria-label={{t "general.manage"}}
                type="button"
                class="manage"
                {{on "click" (fn @setIsManaging true)}}
                data-test-manage
              >
                <FaIcon @icon={{faPenToSquare}} />
              </button>
            {{/if}}
          </div>
        </div>
        {{#if @isManaging}}
          <UserProfileCohortsManager
            @primaryCohort={{this.currentPrimaryCohort}}
            @secondaryCohorts={{this.secondaryCohorts}}
            @addSecondaryCohort={{this.addSecondaryCohort}}
            @removeSecondaryCohort={{this.removeSecondaryCohort}}
            @setPrimaryCohort={{set this "newPrimaryCohort"}}
            @schools={{this.selectedableSchools}}
            @selectedSchool={{this.currentSchool}}
            @setSchool={{set this "selectedSchool"}}
          />
        {{else}}
          <UserProfileCohortsDetails
            @primaryCohort={{this.primaryCohort}}
            @secondaryCohorts={{this.secondaryCohorts}}
          />
        {{/if}}
      {{/if}}
    </div>
  </template>
}
